<script lang="ts" setup>
import { MateCardFactoryGenerateType } from "@/enums/chatEnums";
import { postMateModuleCompleteSteam } from "@/apis/chat";
import { useModule } from "@/stores/module";
import { useGlobalModal } from "@/stores/globalModal";
import { useGlobalUser } from "@/stores/globalUser";
import { Response } from "@/types/api-types";
import { ApiRoleplayQuestionCompleteAnswer } from "@/apis/roleplay";
import { ref, watch } from "vue";
import { AIWriteType } from "@/enums/mateEnums";
import { ApiPostMateMailCompleteSteam } from "@/apis/interaction";
import { showConfirmDialog, showFailToast } from "vant";
import { useI18n } from "vue-i18n";
import { Editor } from "@tiptap/vue-3";
import { marked } from "marked";
import { ApiPostReplyCompleteSteam } from "@/apis/mate";
import api from "@/modules/api";
import {
  MateDispatch,
  MateJournal,
  MateMail,
  MateMemory,
} from "@/types/entity";

const props = defineProps<{
  editor: Editor;
  source: {
    journalzz: MateJournal[];
    mailzz: MateMail[];
    dispatchzz: MateDispatch[];
    memoryzz: MateMemory[];
  };
}>();
const moduleStore = useModule();
const modalStore = useGlobalModal();
const userStore = useGlobalUser();
const { t } = useI18n();
const generateType = ref<MateCardFactoryGenerateType>(
  MateCardFactoryGenerateType.Overwrite
);
const generating = ref(false);
const inspireTxt = ref("");

watch(
  () => modalStore.showRichTextEditor,
  (val) => {
    if (val) {
      generateType.value = MateCardFactoryGenerateType.Overwrite;
      inspireTxt.value = "";
      generating.value = false;
    }
  }
);

const clickGenerate = async () => {
  if (modalStore.richTextAiType === AIWriteType.MateMail) {
    const { senderId, recipientId, subject } = modalStore.richTextOption || {};
    if (!senderId) return showFailToast("请先选择发件人");
    if (!recipientId) return showFailToast("请先选择收件人");
    if (!subject) {
      return modalStore.openTextInputModal({
        title: "请输入信件标题",
        callback: (title?: string) => {
          if (title) {
            modalStore.richTextOption!.subject = title;
            modalStore.richTextCallback2!(title);
            clickGenerate();
          }
        },
      });
    }
  }

  if (
    modalStore.shouldOpenPowerNotEnoughModal({
      spentPowerAmount: 2,
      cb: clickGenerate,
      plusModelRatio: true,
    })
  )
    return;
  try {
    if (
      props.editor.getText() &&
      generateType.value === MateCardFactoryGenerateType.Overwrite
    ) {
      await showConfirmDialog({
        title: t("Message.overwriteConfirm"),
        message: t("Message.overwriteConfirmDesc"),
        confirmButtonText: t("Buttons.confirm"),
        cancelButtonText: t("Buttons.cancel"),
      });
      props.editor.commands.clearContent();
    }
  } catch {
    return;
  }
  onGenerateClickStream();
};

const onGenerateClickStream = () => {
  let isInit = true;
  let text = "";
  let originText = "";

  generating.value = true;

  const isContinue =
    generateType.value === MateCardFactoryGenerateType.Continue;
  const callback = async (res: string | Response<{ text: string }>) => {
    if (isInit) {
      if (isContinue) {
        text = props.editor.getText();
        originText = props.editor.getText();
        props.editor.commands.enter();
      } else {
        text = "";
      }
      isInit = false;
    }
    if (typeof res === "string") {
      try {
        res = JSON.parse(res);
      } catch {
        text += res;
        props.editor.commands.insertContent(res);
        return;
      }
    }
    if (typeof res !== "string" && res.data) {
      const { text: newText } = res.data;
      if (isContinue) {
        text = `${originText}${newText}`;
      } else {
        text = newText;
      }
      generating.value = false;

      props.editor.commands.clearContent();
      text = await marked.parse(text);
      props.editor.commands.insertContent(text);
      if (!userStore.isFreeModel()) userStore.refresh();
    }
  };

  if (modalStore.richTextAiType === AIWriteType.Roleplay) {
    ApiRoleplayQuestionCompleteAnswer(
      modalStore.richTextOption!.questionId!,
      {
        mateId: modalStore.richTextOption!.mateId!,
        type: generateType.value,
        answer:
          generateType.value === MateCardFactoryGenerateType.Continue
            ? props.editor.getText()
            : inspireTxt.value || "",
      },
      callback
    );
  } else if (modalStore.richTextAiType === AIWriteType.Module) {
    const { entity, entityType } = moduleStore.module!;
    postMateModuleCompleteSteam(
      {
        entityId: entity.id,
        entityType: entityType!,
        moduleName: modalStore.richTextModuleName,
        questionText: modalStore.richTextTitle,
        type: generateType.value,
        text:
          generateType.value === MateCardFactoryGenerateType.Continue
            ? props.editor.getText()
            : inspireTxt.value || undefined,
      },
      callback
    );
  } else if (modalStore.richTextAiType === AIWriteType.MateMail) {
    ApiPostMateMailCompleteSteam(
      {
        senderId: modalStore.richTextOption!.senderId!,
        recipientId: modalStore.richTextOption!.recipientId!,
        subject: modalStore.richTextOption!.subject!,
        body:
          generateType.value === MateCardFactoryGenerateType.Continue
            ? props.editor.getText()
            : inspireTxt.value || "",
        type: generateType.value,
        mailIdzz: props.source.mailzz.map((v) => v.id),
        journalIdzz: props.source.journalzz.map((v) => v.id),
        dispatchIdzz: props.source.dispatchzz.map((v) => v.id),
        memoryIdzz: props.source.memoryzz.map((v) => v.id),
      },
      callback
    );
  } else if (modalStore.richTextAiType === AIWriteType.Reply) {
    ApiPostReplyCompleteSteam(
      {
        entityId: modalStore.richTextOption!.replyEntityId!,
        entityType: modalStore.richTextOption!.replyEntityType!,
        parentId: modalStore.richTextOption!.parentId!,
        mateId: modalStore.richTextOption!.mateId!,
        content: inspireTxt.value || "",
        type: generateType.value,
        mailIdzz: props.source.mailzz.map((v) => v.id),
        journalIdzz: props.source.journalzz.map((v) => v.id),
        dispatchIdzz: props.source.dispatchzz.map((v) => v.id),
        memoryIdzz: props.source.memoryzz.map((v) => v.id),
      },
      callback
    );
  } else if (modalStore.richTextAiType === AIWriteType.Moment) {
    api.postMomentComplete(
      {
        mateId: modalStore.richTextOption!.mateId!,
        content: inspireTxt.value || "",
        type: generateType.value,
        mailIdzz: props.source.mailzz.map((v) => v.id),
        journalIdzz: props.source.journalzz.map((v) => v.id),
        dispatchIdzz: props.source.dispatchzz.map((v) => v.id),
        memoryIdzz: props.source.memoryzz.map((v) => v.id),
      },
      callback
    );
  }
};
</script>
<template>
  <div class="flex gap-2 items-center flex-1">
    <!-- <SingleSelect
      :options="GenerateTypeOptions"
      :selected="generateType"
      select-class="btn-xs sm:btn-sm rounded   flex-grow w-full flex-nowrap"
      position="top"
      @update:selected="generateType = $event as MateCardFactoryGenerateType"
    /> -->
    <!-- <div class="badge badge-neutral text-xs">
      {{ $t(`Message.textGeneration.overwrite`) }}
    </div> -->
    <input
      v-if="generateType === MateCardFactoryGenerateType.Overwrite"
      v-model="inspireTxt"
      :disabled="generating"
      :placeholder="
        $t('Message.textGeneration.inspirePlaceholder', {
          label: $t(`module.generateType.${modalStore.richTextAiType}`),
        })
      "
      class="input-sm input input-bordered text-xs flex-grow rounded bg-base-200 flex-1 h-6"
      type="text"
    />
    <div class="flex items-center gap-1">
      <button
        :disabled="generating"
        class="btn btn-primary btn-outline btn-xs rounded sm:btn-sm"
        @click="clickGenerate"
      >
        <i class="iconify mingcute--quill-pen-line" />
        <span v-if="generating" class="loading loading-xs" />
        <div v-else class="flex items-center gap-1">
          {{ $t(`module.generateTypeButton.${modalStore.richTextAiType}`) }}

          <template v-if="!userStore.isFreeModel()">
            <PowerIcon />
            {{ userStore.getPowerCostByModel(2) }}
          </template>
        </div>
      </button>
      <SelectModelBtn />
    </div>
  </div>
</template>
